/**
 * Admin V3 — Partner activate/suspend toggle (AV3 A-7)
 *
 * Per-row partner lifecycle control. Reuses the canonical status endpoints:
 *   - POST /admin/partners/{id}/suspend  (RBAC: admin:partner:suspend)
 *   - POST /admin/partners/{id}/activate (RBAC: admin:partner:update)
 * Sent with an empty body (backend applies its default suspension reason — no
 * personal data placed in the URL). Each row owns its own mutation hook.
 * No backend change. No Billing/pricing/credential touch.
 *
 * NOTE: partner create/edit are intentionally NOT implemented here — the
 * canonical create endpoint entangles a partner credential + billing profile,
 * and the edit endpoint is query-parameter-shaped (see A-7 report). Those are
 * blocked pending owner ratification / a separate backend enablement.
 */
import React from 'react';
import { Loader2, Ban, CheckCircle2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import useAdminMutation from '../../shared/hooks/useAdminMutation';
import usePermissions from '../../shared/hooks/usePermissions';
import { useToast } from '../../shared/hooks/useToast';
import { t, DEFAULT_LOCALE } from '../../i18n';

const PartnerStatusToggle = ({ partner, onDone, locale = DEFAULT_LOCALE }) => {
  const { can } = usePermissions();
  const isActive = (partner?.status || 'active') === 'active';
  const endpoint = isActive
    ? `/admin/partners/${partner?.id || '_'}/suspend`
    : `/admin/partners/${partner?.id || '_'}/activate`;
  const m = useAdminMutation(endpoint, 'post');
  const { showSuccess, showError } = useToast();

  // RBAC: suspend requires admin:partner:suspend; activate requires admin:partner:update.
  const allowed = isActive ? can('admin:partner:suspend') : can('admin:partner:update');
  if (!allowed) return null;

  const toggle = () => m.mutate({}, {
    onSuccess: () => {
      showSuccess(t(isActive ? 'partners.status.suspended' : 'partners.status.activated', locale));
      if (onDone) onDone();
    },
    onError: (e) => showError(e?.response?.data?.detail || t('partners.status.error', locale)),
  });

  return (
    <Button
      variant="ghost"
      size="sm"
      className={`h-7 px-2 text-[12px] ${isActive ? 'text-amber-700 hover:bg-amber-50' : 'text-emerald-700 hover:bg-emerald-50'}`}
      onClick={toggle}
      disabled={m.loading}
      data-testid="adminv3-partner-status-toggle"
    >
      {m.loading
        ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
        : isActive ? <Ban className="h-3.5 w-3.5 mr-1.5" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />}
      {t(isActive ? 'partners.action.suspend' : 'partners.action.activate', locale)}
    </Button>
  );
};

export default PartnerStatusToggle;
